import { StoryFormData } from '../types/StoryFormData';
import api from '../services/api';
import { Story } from '../types/story';

export interface StoryCreationResponse {
  id: string;
  status: 'pending' | 'completed' | 'error';
}

export interface PrintStoryResponse {
  message: string;
  story_id: string;
  status: string;
}

export const createStory = async (storyData: StoryFormData): Promise<StoryCreationResponse> => {
  if (!storyData.characters.length) {
    throw new Error('At least one character is required');
  }

  const transformedData = {
    title: storyData.title,
    description: storyData.description,
    characters: storyData.characters.map(char => char.id),
    is_public: storyData.story_preferences.is_public,
    longer_text: storyData.story_preferences.longer_text,
    rhyming_story: storyData.story_preferences.rhyming_story,
    page_count: storyData.story_preferences.page_count
  };

  const response = await api.post<StoryCreationResponse>('/stories/', transformedData);

  if (!response.data) {
    throw new Error('Failed to create story');
  }

  return response.data;
};


interface StoriesResponse {
  results?: Story[];
  [key: string]: any;
}

export const getUserStories = async (): Promise<Story[]> => {
  try {
    const response = await api.get<Story[] | StoriesResponse>('/stories/');
    
    // Check if response.data is an array
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    // Check if response.data has a results property that is an array
    if (response.data && 'results' in response.data && Array.isArray(response.data.results)) {
      return response.data.results;
    }
    
    // If we can't find an array in the response, log the structure and return empty array
    console.error('Unexpected API response structure:', response.data);
    return [];
  } catch (error) {
    console.error('Failed to fetch user stories:', error);
    throw new Error('Failed to fetch user stories');
  }
};

export const printStory = async (storyId: string): Promise<PrintStoryResponse> => {
  try {
    const response = await api.post<PrintStoryResponse>(`/stories/${storyId}/print/`);
    return response.data;
  } catch (error: any) {
    console.error('Failed to print story:', error);
    
    // Handle specific backend errors
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    
    if (error.response?.data?.story_id?.[0]) {
      throw new Error(error.response.data.story_id[0]);
    }
    
    throw new Error('Failed to print story');
  }
};